import type { Auth0User } from '@nexus/supabase';

import { useQuery } from '@tanstack/react-query';

export type ProfilePictureResolver = (user: Auth0User) => Promise<string | null>;

/**
 * Resolve a profile picture URL for an Auth0 user.
 *
 * Most IdPs (Google, GitHub, Apple, Facebook) forward the photo URL into
 * `user.picture` via Auth0 — no resolver needed. Pass a resolver only for
 * IdPs whose photos require a server-side proxy (e.g. Microsoft Graph).
 *
 * The resolver runs through React Query (10m stale, 15m gc). A null return
 * — or no resolver at all — falls back to `user.picture`.
 */
export function useProfilePicture(user: Auth0User | null, resolver?: ProfilePictureResolver): string | null {
  const { data } = useQuery({
    queryKey: ['profile-picture', user?.id],
    queryFn: () => resolver!(user!),
    enabled: !!user && !!resolver,
    staleTime: 10 * 60 * 1000,
    gcTime: 15 * 60 * 1000,
  });

  if (!user) return null;
  return data ?? user.picture ?? null;
}
